import {
  Component,
  HostListener,
  OnInit,
  ElementRef,
  ViewChild,
  signal,
  computed,
  inject,
  effect,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { UserService } from '@shared/services/user.service';
import { ToasterService } from '@shared/services/toaster.service';

import { APP_CONSTANTS } from '@constants';
import { RoutePaths } from '@routes';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ThemeMode, ThemeService } from '@shared/services/theme.service';

/**
 * @component HeaderLayoutComponent
 * @description Modern header component with dropdown menu, theme toggle and improved UX
 */
@Component({
  selector: 'header-layout',
  templateUrl: './header-layout.component.html',
  styleUrls: ['./header-layout.component.css'],
  standalone: true,
  imports: [CommonModule],
})
export class HeaderLayoutComponent implements OnInit, OnDestroy {
  /** Dropdown state */
  isDropdownOpen = signal(false);

  /** Whether logout overlay is shown */
  showLogoutOverlay = signal(false);

  /** Injected services */
  private authService = inject(AuthenticationService);
  private userService = inject(UserService);
  private toaster = inject(ToasterService);
  private router = inject(Router);
  private themeService = inject(ThemeService); // Theme service

  /** Theme-related signals */
  currentTheme = signal<ThemeMode>('system');
  isDarkMode = signal<boolean>(false);
  
  /** Subscriptions */
  private subscriptions = new Subscription();

  /** User data signals */
  userName = this.userService.userName;
  userEmail = this.userService.userEmail;
  userRole = this.userService.userRole;
  userSubscription = this.userService.userSubscription;

  /** Theme-related computed properties */
  themeDisplayName = computed(() => {
    const theme = this.currentTheme();
    const systemSuffix = theme === 'system' 
      ? ` (${this.isDarkMode() ? 'Dark' : 'Light'})` 
      : '';
    return this.themeService.getThemeLabel(theme) + systemSuffix;
  });

  nextThemeLabel = computed(() => {
    const current = this.currentTheme();
    switch (current) {
      case 'light':
        return 'Dark';
      case 'dark':
        return 'System';
      case 'system':
        return 'Light';
      default:
        return 'Dark';
    }
  });

  /** Computed user initials */
  userInitials = computed(() => {
    const name = this.userName();
    if (!name || name.trim() === '' || name === 'Unknown') return 'U';

    const names = name
      .trim()
      .split(' ')
      .filter((n) => n.length > 0);
    if (names.length === 0) return 'U';

    return names
      .map((n) => n.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  });

  /** Formatted subscription label */
  userSubscriptionLabel = computed(() => {
    const sub = this.userSubscription();
    if (!sub || sub === '') return 'Free';
    return sub.charAt(0).toUpperCase() + sub.slice(1);
  });

  /** Formatted role label */
  userRoleLabel = computed(() => {
    const role = this.userRole();
    if (!role || role === '') return 'User';
    return role.charAt(0).toUpperCase() + role.slice(1);
  });

  /** Check if user is admin */
  isAdmin = computed(() => {
    return this.userRole()?.toLowerCase() === 'admin';
  });

  /** Check if user is subadmin */
  isSubadmin = computed(() => {
    return this.userRole()?.toLowerCase() === 'subadmin';
  });

  /** Dropdown container reference */
  @ViewChild('dropdownContainer', { static: false })
  dropdownContainer!: ElementRef;

  /** Effect to initialize user data */
  private syncUserDataEffect = effect(() => {
    if (!this.userName() && !this.userEmail()) {
      this.initializeUserDataFromCookie();
    }
  });

  ngOnInit(): void {
    this.initializeUserDataFromCookie();
    this.setupThemeSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Setup theme subscriptions
   */
  private setupThemeSubscriptions(): void {
    // Subscribe to theme changes
    this.subscriptions.add(
      this.themeService.theme$.subscribe(theme => {
        this.currentTheme.set(theme);
      })
    );

    // Subscribe to dark mode changes
    this.subscriptions.add(
      this.themeService.isDarkMode$.subscribe(isDark => {
        this.isDarkMode.set(isDark);
      })
    );

    // Initialize current values
    this.currentTheme.set(this.themeService.currentTheme);
    this.isDarkMode.set(this.themeService.isDarkMode);
  }

  /**
   * Toggle theme
   */
  toggleTheme(): void {
    const current = this.currentTheme();
    let newTheme: ThemeMode;

    switch (current) {
      case 'light':
        newTheme = 'dark';
        break;
      case 'dark':
        newTheme = 'system';
        break;
      case 'system':
      default:
        newTheme = 'light';
        break;
    }

    this.themeService.setTheme(newTheme);
  }

  /**
   * Get theme icon class
   */
  getThemeIconClass(): string {
    return this.themeService.getThemeIcon(this.currentTheme());
  }

  /**
   * Get theme button classes
   */
  getThemeButtonClasses(): string {
    const baseClasses = 'relative flex items-center justify-center w-10 h-10 rounded-xl border border-grounded/30 dark:border-stone-600 hover:scale-105 transition-all duration-300 backdrop-blur-sm group';
    const theme = this.currentTheme();
    
    switch (theme) {
      case 'light':
        return `${baseClasses} bg-white/80 text-soulfire hover:bg-sacred/20 hover:text-bloom shadow-lg`;
      case 'dark':
        return `${baseClasses} bg-white/80 dark:bg-stone-700/80 text-soulfire dark:text-fuchsia-400 hover:bg-sacred/20 dark:hover:bg-stone-600/50 hover:text-bloom dark:hover:text-fuchsia-300 shadow-lg dark:shadow-fuchsia-900/10`;
      case 'system':
        return `${baseClasses} bg-white/80 dark:bg-stone-700/80 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-stone-600/50 hover:text-purple-500 dark:hover:text-purple-300 shadow-lg dark:shadow-fuchsia-900/10`;
      default:
        return baseClasses;
    }
  }

  /**
   * Get theme indicator dot color
   */
  getThemeIndicatorClass(): string {
    const theme = this.currentTheme();
    switch (theme) {
      case 'light':
        return 'bg-yellow-400';
      case 'dark':
        return 'bg-blue-600 dark:bg-blue-400';
      case 'system':
        return 'bg-purple-500 dark:bg-purple-400';
      default:
        return 'bg-gray-400';
    }
  }

  /**
   * Initialize user data from cookies
   */
  private initializeUserDataFromCookie(): void {
    try {
      const userCookie = this.authService.getCookie(
        APP_CONSTANTS.COOKIE_NAMES.USER
      );
      if (userCookie) {
        const user = JSON.parse(userCookie);

        // Only set if current data is empty
        if (!this.userName() || !this.userEmail()) {
          this.userService.setUser({
            name: user.name || 'Unknown User',
            email: user.email || 'no-email@example.com',
            role: user.role || 'user',
            subscription: user.subscription || 'free',
          });
        }
      } else {
        // Set default values if no cookie exists
        this.userService.setUser({
          name: 'Guest User',
          email: 'guest@example.com',
          role: 'user',
          subscription: 'free',
        });
      }
    } catch (error) {
      this.toaster.showError('Failed to restore user session');

      // Clear invalid cookie and set defaults
      this.authService.deleteCookie(APP_CONSTANTS.COOKIE_NAMES.USER);
      this.userService.setUser({
        name: 'Unknown User',
        email: 'error@example.com',
        role: 'user',
        subscription: 'free',
      });
    }
  }

  /**
   * Toggle dropdown menu
   */
  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const currentState = this.isDropdownOpen();
    this.isDropdownOpen.set(!currentState);
  }

  /**
   * Close dropdown menu
   */
  closeDropdown(): void {
    if (this.isDropdownOpen()) {
      this.isDropdownOpen.set(false);
    }
  }

  /**
   * Handle clicks outside dropdown to close it
   */
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (
      this.isDropdownOpen() &&
      this.dropdownContainer?.nativeElement &&
      !this.dropdownContainer.nativeElement.contains(event.target as Node)
    ) {
      this.closeDropdown();
    }
  }

  /**
   * Handle escape key to close dropdown
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }

  /**
   * Navigate to user profile
   */
  async navigateToProfile(): Promise<void> {
    this.closeDropdown();

    try {
      const success = await this.router.navigate([`/${RoutePaths.PROFILE}`]);
      if (!success) {
        this.toaster.showError('Unable to navigate to profile');
      }
    } catch (error) {
      this.toaster.showError('Navigation error occurred');
    }
  }

  /**
   * Navigate to settings
   */
  async navigateToSettings(): Promise<void> {
    this.closeDropdown();

    try {
      const success = await this.router.navigate([`/${RoutePaths.SETTINGS}`]);
      if (!success) {
        this.toaster.showError('Unable to navigate to settings');
      }
    } catch (error) {
      this.toaster.showError('Navigation error occurred');
    }
  }

  /**
   * Handle user logout
   */
  async logout(): Promise<void> {
    this.closeDropdown();

    try {
      // Show loading overlay
      this.showLogoutOverlay.set(true);

      // Clear user data
      this.userService.clearUser();

      // Clear session data
      this.clearUserSession();

      // Call authentication service logout
      await this.authService.handleLogout();

      // Small delay for UX, then hide overlay
      setTimeout(() => {
        this.showLogoutOverlay.set(false);
      }, 1500);
    } catch (error) {
      this.showLogoutOverlay.set(false);
      this.toaster.showError('Logout failed, please try again');

      // Force fallback logout
      this.forceLogout();
    }
  }

  /**
   * Force logout as fallback
   */
  private forceLogout(): void {
    try {
      this.clearUserSession();
      window.location.href = '/login';
    } catch (error) {
      this.toaster.showError('Critical error occurred');
      window.location.reload();
    }
  }

  /**
   * Clear all user session data
   */
  private clearUserSession(): void {
    try {
      // List of cookies to clear
      const cookiesToClear = [
        APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN,
        APP_CONSTANTS.COOKIE_NAMES.USER,
        'session_id',
        'refresh_token',
        'access_token',
        'token',
      ];

      // Clear each cookie
      cookiesToClear.forEach((cookieName) => {
        this.authService.deleteCookie(cookieName);
      });

      // Clear browser storage
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (storageError) {
        // Silent fail for storage clearing
      }

      // Clear user service data
      this.userService.clearUser();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if user is online
   */
  isUserOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Update user data
   */
  updateUserData(userData: {
    name: string;
    email: string;
    role: string;
    subscription: string;
  }): void {
    this.userService.setUser(userData);
  }

  /**
   * Get current timestamp for debugging
   */
  getCurrentTimestamp(): string {
    return new Date().toISOString();
  }
}